import React, { useEffect } from 'react';

interface BasicModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  fullWidth?: boolean;
  showHeader?: boolean;
}

/**
 * A basic, reliable modal implementation using simple techniques
 */
const BasicModal: React.FC<BasicModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  fullWidth = false,
  showHeader = true 
}) => {
  // Close the modal when Escape key is pressed
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        console.log('BasicModal: Escape key pressed');
        onClose();
      }
    };

    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    // Log when modal is opened
    console.log('BasicModal: Modal opened');

    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleEscape);
      console.log('BasicModal: Modal cleanup');
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Handler for the backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop (not its children)
    if (e.target === e.currentTarget) {
      console.log('BasicModal: Backdrop clicked');
      onClose();
    }
  };

  // Explicit handler for close button
  const handleCloseClick = () => {
    console.log('BasicModal: Close button clicked');
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
      style={{ backdropFilter: 'blur(2px)' }}
    >
      <div 
        className={`bg-white rounded-lg ${fullWidth ? 'w-[95%]' : 'max-w-4xl'} h-[90vh] flex flex-col border-2 border-gray-200 shadow-xl`}
        onClick={(e) => e.stopPropagation()}
      >
        {showHeader && (
          <div className="flex flex-col p-6 bg-white border-b sticky top-0 z-20">
            <div className="flex justify-between items-center w-full">
              <div className="flex-1" />
              <div className="flex gap-4 justify-center flex-1 whitespace-nowrap">
                <h2 className="text-xl font-semibold text-gray-600">{title}</h2>
              </div>
            
              <div className="flex-1 flex justify-end">
                <button 
                  onClick={handleCloseClick}
                  className="text-gray-500 hover:text-gray-700 p-2"
                  aria-label="Close"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mt-2">Persona Details</h2>
          </div>
        )}

        <div className="overflow-y-auto p-6 flex-1">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BasicModal; 